import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import StepModal from '@swyftx/aviary/molecules/StepModal';
import { ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { UpdateMarginPosition } from '@swyftx/margin/build/types';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useMarginPositionDetails, useUpdateMarginPosition } from 'src/lib/margin/hooks/positions';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';
import { getPositionOrderDirection } from 'src/lib/margin/utils';

import { MarginPositionDetailsProtectionOrder } from './MarginPositionDetailsProtectionOrder';
import { useMarginPositionDetailsProtectionOrderStep } from './useMarginPositionDetailsProtectionOrderStep';
import {
  MarginInstrument,
  EstimatedMarginProfitLoss,
  TotalMarginRequired,
  MarginPriceInput,
  MarginLotsInput,
} from '../../../../General';

type Props = {
  positionId?: string;
  protectionOrderType?: ProtectionOrderVariant;
  onClose: () => void;
};

const MarginPositionDetailsProtectionOrdersStep: React.FC<Props> = observer(
  ({ onClose, positionId, protectionOrderType }) => {
    const {
      lots,
      setLots,
      price,
      audPrice,
      setPrice,
      position,
      positionMarket,
      isValidUpdateMarginProtection,
      newTakeProfitPrice,
      takeProfitToggled,
      newStopLossPrice,
      stopLossToggled,
      setNewTakeProfitPrice,
      setNewStopLossPrice,
      onTakeProfitToggled,
      onStopLossToggled,
    } = useMarginPositionDetailsProtectionOrderStep({ positionId, protectionOrderType });
    const { t } = useTranslation('margin');
    const [loading, setLoading] = useState<boolean>(false);
    const { updatePosition, invalidateCache } = useUpdateMarginPosition();
    const { getCurrencyRate } = useMarginPrices();
    const { currency } = useMarginAccount();
    const {
      type,
      isPending,
      isOpen,
      secondaryAsset,
      currentPrice,
      currentAudPrice,
      isLimitOrder,
      isStopOrder,
      side,
      tradeType,
    } = useMarginPositionDetails({ positionId });
    const { addSnackBar } = useSnackBars();
    const { openModal } = useModal();
    const { symbol } = useMarginInstrumentDetails({ instrument: positionMarket });

    const onUpdatePosition = useCallback(async () => {
      if (!position) return;

      try {
        setLoading(true);

        const positionUpdate: UpdateMarginPosition = {
          takeProfit: newTakeProfitPrice ? newTakeProfitPrice : position.takeProfit ? '0' : undefined,
          stopLoss: newStopLossPrice ? newStopLossPrice : position.stopLoss ? '0' : undefined,
          contracts: lots,
          limitPrice: isLimitOrder && isPending ? price : undefined,
          stopPrice: isStopOrder && isPending ? price : undefined,
        };

        await updatePosition(position.positionId, positionUpdate);

        addSnackBar({
          variant: 'success',
          content: `${symbol} ${position.sell ? 'short' : 'long'} position updated`,
          showIcon: true,
          label: 'View order',
          duration: 5_000,
          onClick: () => openModal(Modals.MarginPositionDetails, { positionId: position.positionId }),
        });
      } catch (e) {
        addSnackBar({
          variant: 'error',
          content: `Failed to update ${symbol} position`,
          showIcon: true,
          label: 'View order',
          duration: 5_000,
        });

        invalidateCache();
      } finally {
        setLoading(false);
        onClose();
      }
    }, [
      addSnackBar,
      invalidateCache,
      isLimitOrder,
      isStopOrder,
      lots,
      newStopLossPrice,
      newTakeProfitPrice,
      onClose,
      openModal,
      position,
      price,
      symbol,
      updatePosition,
    ]);

    if (!position) return null;

    return (
      <StepModal.Step
        key={MarginPositionDetailsStep.ManageProtectionOrders}
        onClose={onClose}
        hideActions
        title={isPending ? 'Edit order' : 'Edit position'}
        position='dynamic'
      >
        <FlexLayout direction='column' spacing={16} className='h-full px-24 pb-0' justifyContent='space-between'>
          <FlexLayout direction='column' spacing={12} className='h-full'>
            <FlexLayout direction='column' spacing={8}>
              <ReviewItem title='Instrument'>
                <MarginInstrument market={positionMarket} size='medium' weight='bold' />
              </ReviewItem>
              {!!type && isPending && (
                <ReviewItem title='Type'>
                  <Body color='primary' weight='bold'>
                    {t(`tradeType.${type}`)}
                  </Body>
                </ReviewItem>
              )}
              <ReviewItem title='Side'>
                <Body color='primary' weight='bold'>
                  {t(`tradeSide.${getPositionOrderDirection(position)}`)}
                </Body>
              </ReviewItem>

              <ReviewItem title='Current price'>
                <Numeric color='primary' weight='bold'>
                  {formatCurrency(currentPrice, undefined, { priceScale: positionMarket?.precision })}
                </Numeric>
              </ReviewItem>

              {isOpen && position.marginUsed && (
                <ReviewItem title='Used margin' underline='dashed'>
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(position.marginUsed, currency, { appendCode: true, hideCode: false })}
                  </Numeric>
                </ReviewItem>
              )}

              <Separator className='my-8' />
            </FlexLayout>

            {positionMarket && (
              <MarginPriceInput
                readOnly={isOpen}
                disabled={isOpen}
                isOpenPosition={isOpen}
                price={price.toString()}
                onChange={setPrice}
                tradeType={type}
                tradeSide={getPositionOrderDirection(position)}
                market={positionMarket}
              />
            )}

            {secondaryAsset && positionMarket && side !== undefined && (
              <MarginLotsInput
                readOnly={isOpen}
                disabled={isOpen}
                lots={lots}
                onChange={setLots}
                asset={secondaryAsset}
                maxMarginPosition={Big(positionMarket.maxLots)}
                market={positionMarket}
                price={isPending ? audPrice : currentAudPrice?.toString()}
              />
            )}

            <MarginPositionDetailsProtectionOrder
              tradeSide={getPositionOrderDirection(position)}
              tradeType={type}
              position={position}
              protectionOrderType={ProtectionOrderVariant.StopLoss}
              checked={stopLossToggled}
              value={newStopLossPrice}
              price={price}
              onChange={setNewStopLossPrice}
              onCheckedChanged={onStopLossToggled}
              placeholder={isPending ? price.toString() : currentPrice?.toString()}
            />

            <MarginPositionDetailsProtectionOrder
              tradeSide={getPositionOrderDirection(position)}
              tradeType={type}
              position={position}
              checked={takeProfitToggled}
              price={price}
              protectionOrderType={ProtectionOrderVariant.TakeProfit}
              value={newTakeProfitPrice}
              onChange={setNewTakeProfitPrice}
              onCheckedChanged={onTakeProfitToggled}
              placeholder={isPending ? price.toString() : currentPrice?.toString()}
            />
          </FlexLayout>

          <FlexLayout
            direction='column'
            spacing={16}
            className='sticky bottom-0 bg-color-background-surface-primary pb-16'
          >
            <Separator />
            <FlexLayout direction='column' spacing={8}>
              {stopLossToggled && positionMarket && (
                <ReviewItem
                  title={t('position.estimatedLoss.title')}
                  underline='dashed'
                  tooltip={t('position.estimatedLoss.tooltip')}
                >
                  <EstimatedMarginProfitLoss
                    lots={lots}
                    price={Big(price).times(getCurrencyRate()).toString()}
                    currentPrice={Big(currentPrice).toString()}
                    protectionPrice={Number(newStopLossPrice)}
                    protectionVariant={ProtectionOrderVariant.StopLoss}
                    instrument={positionMarket}
                    side={side}
                    tradeType={tradeType}
                  />
                </ReviewItem>
              )}
              {takeProfitToggled && positionMarket && (
                <ReviewItem
                  title={t('position.estimatedProfit.title')}
                  underline='dashed'
                  tooltip={t('position.estimatedProfit.tooltip')}
                >
                  <EstimatedMarginProfitLoss
                    lots={lots}
                    price={Big(price).times(getCurrencyRate()).toString()}
                    currentPrice={Big(currentPrice).toString()}
                    protectionPrice={Number(newTakeProfitPrice)}
                    instrument={positionMarket}
                    side={side}
                    tradeType={tradeType}
                    protectionVariant={ProtectionOrderVariant.TakeProfit}
                  />
                </ReviewItem>
              )}
              {isPending && price && (
                <TotalMarginRequired
                  title={t('position.totalMarginRequired.title')}
                  lots={lots}
                  price={Big(price).times(getCurrencyRate()).toString()}
                  contractSize={positionMarket?.contractSize}
                  maxLeverage={positionMarket?.maxLeverage}
                />
              )}
            </FlexLayout>
            <FlexLayout justifyContent='end' spacing={16}>
              <Button variant='outlined' size='lg' onClick={onClose} className='hidden w-full sm:block'>
                Cancel
              </Button>
              <Button
                color='accent'
                size='lg'
                className='w-full'
                disabled={!isValidUpdateMarginProtection}
                onClick={onUpdatePosition}
                loading={loading}
              >
                Update order
              </Button>
            </FlexLayout>
          </FlexLayout>
        </FlexLayout>
      </StepModal.Step>
    );
  },
);

export { MarginPositionDetailsProtectionOrdersStep };
